import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { ModalActionButtonStyles } from "../Common/styles/modal";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { ClearIcon, DatePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import {
  CommonDrawerStyle,
  DrawerForm,
  DrawerHeader,
} from "../Common/styles/drawer";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

type Props = {
  closeModal: () => void;
  startDate: string | null;
  setStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  endDate: string | null;
  setEndDate: React.Dispatch<React.SetStateAction<string | null>>;
  eventType: string;
  setEventType: React.Dispatch<React.SetStateAction<string>>;
  type: string;
  setPage: React.Dispatch<React.SetStateAction<number>>;
};

let schema = yup.object().shape({
  eventType: yup.string().optional(),
  startDate: yup.string().optional(),
  endDate: yup.string().optional(),
});

const AuditTrailFilterDrawer = ({
  closeModal,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  eventType,
  setEventType,
  setPage,
  type,
}: Props) => {
  const { id: studyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [eventList, setEventList] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let url: string;
        if (type === "platform_audits") {
          url = `/platform/audit-trail/filters`;
        } else {
          url = `/audit-trail/filters`;
        }
        if (type === "study_audits") {
          url += `?studyId=${studyId}`;
        }
        const res: AxiosResponse = await http.get(url);

        const listData = res?.data?.data?.eventTypes;

        const newData = listData?.map((item: string) => {
          const label = item
            ?.replace(/_/g, " ")
            .toLowerCase()
            .replace(/^\w/, (c) => c.toUpperCase());
          return {
            label: label || "",
            value: item || "",
          };
        });

        setEventList(newData || []);
      } catch (error) {
        errorToastMessage(error as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [type, studyId]);

  const submitHandler = async (values: any) => {
    const hasChanged =
      values?.startDate !== startDate ||
      values?.endDate !== endDate ||
      values?.eventType !== eventType;
    if (hasChanged) {
      setPage(0);
    }
    setStartDate(values?.startDate);
    setEndDate(values?.endDate);
    setEventType(values?.eventType);

    closeModal();
  };

  return (
    <Box sx={CommonDrawerStyle}>
      <DrawerHeader closeDrawer={closeModal} title={"Filter By"} />
      {!loading ? (
        <Formik
          initialValues={{
            startDate: startDate || "",
            endDate: endDate || "",
            eventType: eventType || "",
          }}
          validationSchema={schema}
          onSubmit={(values) => {
            submitHandler(values);
          }}
        >
          {({ errors, touched, setFieldValue, values }) => (
            <Form style={DrawerForm}>
              <Box sx={{ overflow: "auto", pr: 0.5 }}>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="startDate">
                    Start Date
                  </FormLabel>
                  <DatePicker
                    format="dd/MM/yyyy"
                    value={
                      values?.startDate
                        ? DateTime.fromFormat(values?.startDate, "dd/MM/yyyy")
                        : null
                    }
                    onChange={(newValue: any) => {
                      if (newValue) {
                        setFieldValue(
                          "startDate",
                          newValue.toFormat("dd/MM/yyyy")
                        );
                      } else {
                        setFieldValue("startDate", "");
                      }
                    }}
                    sx={{
                      border:
                        touched?.startDate && errors?.startDate
                          ? "1px solid red"
                          : null,
                      borderRadius:
                        touched?.startDate && errors?.startDate ? "8px" : null,
                    }}
                    slots={{
                      clearButton: IconButton,
                      clearIcon: ClearIcon,
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => setFieldValue("startDate", ""),
                      },
                    }}
                  />
                  <FormHelperText
                    error={
                      touched?.startDate && errors?.startDate ? true : false
                    }
                  >
                    {touched?.startDate && errors?.startDate
                      ? (errors?.startDate as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle} htmlFor="endDate">
                    End Date
                  </FormLabel>
                  <DatePicker
                    minDate={
                      values?.startDate
                        ? DateTime.fromFormat(values?.startDate, "dd/MM/yyyy")
                        : null
                    }
                    format="dd/MM/yyyy"
                    value={
                      values?.endDate
                        ? DateTime.fromFormat(values?.endDate, "dd/MM/yyyy")
                        : null
                    }
                    onChange={(newValue: any) => {
                      if (newValue) {
                        setFieldValue(
                          "endDate",
                          newValue.toFormat("dd/MM/yyyy")
                        );
                      } else {
                        setFieldValue("endDate", "");
                      }
                    }}
                    sx={{
                      border:
                        touched?.endDate && errors?.endDate
                          ? "1px solid red"
                          : null,
                      borderRadius:
                        touched?.endDate && errors?.endDate ? "8px" : null,
                    }}
                    slots={{
                      clearButton: IconButton,
                      clearIcon: ClearIcon,
                    }}
                    slotProps={{
                      field: {
                        clearable: true,
                        onClear: () => setFieldValue("endDate", ""),
                      },
                    }}
                  />
                  <FormHelperText
                    error={touched?.endDate && errors?.endDate ? true : false}
                  >
                    {touched?.endDate && errors?.endDate
                      ? (errors?.endDate as string)
                      : " "}
                  </FormHelperText>
                </FormControl>

                <FormControl sx={InputWrapper}>
                  <FormLabel sx={LabelStyle}>Event Type</FormLabel>
                  <Select
                    value={values.eventType}
                    onChange={(e) => {
                      setFieldValue("eventType", e.target.value);
                    }}
                    fullWidth
                    id="eventType"
                    displayEmpty
                    renderValue={
                      values.eventType !== ""
                        ? undefined
                        : () => (
                            <Typography sx={{ color: "#c1cccf" }}>
                              Select
                            </Typography>
                          )
                    }
                    endAdornment={
                      values.eventType !== "" && (
                        <InputAdornment
                          sx={{ marginRight: "10px" }}
                          position="end"
                        >
                          <IconButton
                            onClick={() => {
                              setFieldValue("eventType", "");
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {eventList.map((option: any) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText
                    error={
                      touched?.eventType && errors?.eventType ? true : false
                    }
                  >
                    {touched?.eventType && errors?.eventType
                      ? (errors?.eventType as string)
                      : " "}
                  </FormHelperText>
                </FormControl>
              </Box>

              <Box sx={{ mt: "auto" }}>
                <Divider />

                <Box sx={{ ...ModalActionButtonStyles, mt: 2, mb: 2 }}>
                  <>
                    <Button
                      onClick={() => {
                        setFieldValue("startDate", "");
                        setFieldValue("endDate", "");
                        setFieldValue("eventType", "");
                      }}
                      variant="outlined"
                    >
                      Clear All
                    </Button>
                    <Button onClick={closeModal} variant="outlined">
                      Cancel
                    </Button>
                    <Button type="submit" variant="contained">
                      Apply
                    </Button>
                  </>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      ) : (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default AuditTrailFilterDrawer;
